import { Controller } from "@hotwired/stimulus"

// Drives the "Suggest best warehouse" UI on the Edit Order page.
// One click fetches /orders/:id/fulfillment_suggestion, renders a stack of
// per-warehouse coverage chips, and (if a warehouse fulfills 100% of the
// order) sets the warehouse select to that warehouse.
//
// Values:
//   url   — the JSON endpoint to fetch
//   target — id of the warehouse <select> to set
export default class extends Controller {
    static values  = { url: String, target: String }
    static targets = ["panel", "summary", "button"]

    async suggest() {
        if (this.hasButtonTarget) {
            this.buttonTarget.disabled = true
            this.buttonTarget.textContent = "Analyzing stock…"
        }
        try {
            const res = await fetch(this.urlValue, {
                headers: { "Accept": "application/json" },
                credentials: "same-origin"
            })
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const data = await res.json()
            this.render(data)
        } catch (e) {
            this.summaryTarget.innerHTML =
                `<div class="text-danger" style="color: #dc2626;">Couldn't compute coverage: ${e.message}</div>`
        } finally {
            if (this.hasButtonTarget) {
                this.buttonTarget.disabled = false
                this.buttonTarget.textContent = "Suggest best warehouse"
            }
        }
    }

    render(data) {
        // Headline summary — "Irvine fulfills 3/3" or "No single warehouse covers everything"
        if (data.requires_split && (data.unfulfillable_lines || []).length === 0) {
            this.summaryTarget.innerHTML =
                `<strong>Split needed:</strong> no single warehouse has every line. ` +
                `Best single pick: <em>${this.esc(data.best_warehouse_name) || '—'}</em> ` +
                `covers ${data.coverage?.lines_covered ?? 0}/${data.coverage?.lines_total ?? 0} lines.`
        } else if ((data.unfulfillable_lines || []).length > 0) {
            const productList = data.unfulfillable_lines.map(l => `${this.esc(l.product_name)} (short ${this.esc(l.short)})`).join(", ")
            this.summaryTarget.innerHTML =
                `<strong style="color: #dc2626;">Backorder:</strong> insufficient stock company-wide for ${productList}.`
        } else {
            this.summaryTarget.innerHTML =
                `<strong style="color: #166534;">✓ ${this.esc(data.best_warehouse_name)}</strong> ` +
                `fulfills the entire order (${data.coverage?.lines_total ?? 0} line${data.coverage?.lines_total === 1 ? '' : 's'}).`
        }

        // Per-warehouse chip stack
        const rows = (data.per_warehouse || []).map(w => this.chipHTML(w, data.best_warehouse_id)).join("")
        this.panelTarget.innerHTML = rows

        // If a single warehouse fulfills 100%, auto-select it. Otherwise leave
        // whatever the user already had — we don't want to silently change
        // their pick when the answer is "you have to split."
        if (!data.requires_split && data.best_warehouse_id) {
            this.setWarehouse(data.best_warehouse_id)
        }
    }

    chipHTML(w, bestId) {
        const isPerfect    = w.fulfills_all
        const isBest       = w.warehouse_id === bestId
        const bg           = isPerfect ? "#dcfce7" : (w.lines_covered === 0 ? "#fee2e2" : "#fef3c7")
        const color        = isPerfect ? "#166534" : (w.lines_covered === 0 ? "#991b1b" : "#92400e")
        const icon         = isPerfect ? "✓" : (w.lines_covered === 0 ? "✗" : "⚠")
        const shortInfo    = (w.short_lines || []).map(l => `${this.esc(l.product_name)} short ${this.esc(l.short)}`).join(", ")
        const star         = isBest ? "★ " : ""

        // If this chip's warehouse is ALREADY the one selected on the form,
        // render a "Selected" badge instead of the "Use this" button — clicking
        // Use this on the same warehouse used to do nothing visible, which
        // read as a broken button.
        const select       = document.getElementById(this.targetValue)
        const currentId    = select ? select.value : null
        const isCurrent    = currentId && String(currentId) === String(w.warehouse_id)
        const useLink      = isCurrent
            ? `<span style="font-size: 0.75rem; opacity: 0.75;">Selected</span>`
            : `<button type="button" data-action="click->fulfillment-suggester#use" data-wh-id="${w.warehouse_id}" style="border: 0; background: transparent; color: var(--color-primary-600); cursor: pointer; padding: 0; font-size: 0.75rem; text-decoration: underline;">Use this</button>`

        return `
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0.75rem; background: ${bg}; color: ${color}; border-radius: 0.375rem; margin-bottom: 0.375rem; font-size: 0.875rem;">
            <div>
              <strong>${icon} ${star}${this.esc(w.warehouse_name)}</strong>
              <span style="opacity: 0.8;"> &middot; ${w.lines_covered}/${w.lines_total} lines &middot; ${w.total_available} units in stock</span>
              ${shortInfo ? `<div style="font-size: 0.7rem; opacity: 0.85; margin-top: 0.125rem;">Short: ${shortInfo}</div>` : ""}
            </div>
            <div>${useLink}</div>
          </div>
        `
    }

    use(event) {
        const id = event.currentTarget.getAttribute("data-wh-id")
        this.setWarehouse(id)
        // Flip the chip we just clicked from "Use this" → "Selected" without
        // re-fetching. Cheaper than calling suggest() again and keeps the
        // existing coverage numbers visible.
        if (this.hasPanelTarget) {
            this.panelTarget.querySelectorAll('button[data-action*="fulfillment-suggester#use"]').forEach(btn => {
                if (btn.getAttribute("data-wh-id") === id) {
                    const badge = document.createElement("span")
                    badge.style.cssText = "font-size: 0.75rem; opacity: 0.75;"
                    badge.textContent = "Selected"
                    btn.replaceWith(badge)
                }
            })
        }
    }

    // Product and warehouse names are operator-supplied and arrive as raw JSON
    // (Rails' entity escaping is undone by res.json()), so every server string
    // interpolated into innerHTML above goes through here — otherwise a product
    // named `<img src=x onerror=…>` executes in the session of whoever clicks
    // "Suggest best warehouse". Same helper the sibling controllers use.
    esc(s) {
        const d = document.createElement("div")
        d.textContent = s == null ? "" : String(s)
        return d.innerHTML
    }

    setWarehouse(id) {
        const select = document.getElementById(this.targetValue)
        if (!select) return
        const changed = String(select.value) !== String(id)
        select.value = id
        // Notify any listeners (Stimulus controllers, change-driven validators).
        select.dispatchEvent(new Event("change", { bubbles: true }))

        // Brief visual flash on the dropdown so the user gets feedback even
        // when the underlying value didn't actually change.
        const prev = select.style.boxShadow
        select.style.transition = "box-shadow 0.25s ease"
        select.style.boxShadow  = "0 0 0 3px rgba(34, 197, 94, 0.5)"
        setTimeout(() => { select.style.boxShadow = prev }, 700)

        if (changed && this.hasSummaryTarget) {
            const opt = select.options[select.selectedIndex]
            const name = opt ? opt.text : "warehouse"
            // Append a small confirmation under the existing summary text.
            const note = document.createElement("div")
            note.style.cssText = "color: #166534; font-size: 0.75rem; margin-top: 0.25rem;"
            note.textContent = `✓ Switched fulfillment warehouse to ${name}.`
            this.summaryTarget.appendChild(note)
            setTimeout(() => note.remove(), 2500)
        }
    }
}
